import { useAppDispatch, useAppSelector } from '../../Redux/Store';
import {
    fetchSex,
    fetchLots,
    fetchTechs,
    fetchRiders,
    fetchDiagnosis,
    fetchMedicalItems,
    fetchTreatmentDetailsService,
    fetchHospitalPens,
} from '../../Redux/Actions/treatmentsActions';
import { useEffect } from 'react';
import { useFeedlotId } from '../../commonHooks/useFeedlotId';
import { useParams } from 'react-router-dom';
import {
    getGeneratedInvoiceCutoffDate,
    handleFindPenInformation,
    thisDateWithinCutoff_deprecated,
} from '../../helpers';
import { IGenericSetState } from '../../utils/Interface/CommonInterface';
import { IMedicalItem } from '../../utils/Interface/TreatmentsInterface';
import { treatmentActions } from '../../Redux/Reducer/treatmentsSlice';
import { Roles } from '../../utils/constants/RoleConstants';
import useAuthContext from '../../apps/common/modules/auth/hooks/useAuthContext';

export const useTreatmentHook = (
    setInvoiceAlreadyGeneratedForThisRecord: IGenericSetState<boolean>,
) => {
    const dispatch = useAppDispatch();
    const feedlotId = useFeedlotId();
    const { cattleTreatmentId } = useParams();
    const { editTreatmentsDetails, generatedInvoiceCutoffDate } =
        useAppSelector(state => state.treatments);

    useEffect(() => {
        const onTreatmentFormOpened = () => {
            if (feedlotId) {
                dispatch(fetchSex());
                dispatch(fetchLots(feedlotId));
                dispatch(fetchTechs(feedlotId));
                dispatch(fetchRiders(feedlotId));
                dispatch(fetchDiagnosis(feedlotId));
                dispatch(fetchHospitalPens(feedlotId));
                if (!cattleTreatmentId) {
                    dispatch(fetchMedicalItems(feedlotId));
                }
            }
        };
        onTreatmentFormOpened();
    }, [feedlotId, dispatch, cattleTreatmentId]);

    useEffect(() => {
        const onUpdatingTreatment = () => {
            if (cattleTreatmentId && feedlotId) {
                dispatch(
                    fetchTreatmentDetailsService(+cattleTreatmentId, feedlotId),
                );
            }
        };
        onUpdatingTreatment();
    }, [cattleTreatmentId, feedlotId, dispatch]);

    useEffect(() => {
        if (feedlotId && cattleTreatmentId) {
            (async () => {
                const cutoffDate = await getGeneratedInvoiceCutoffDate(
                    +feedlotId,
                    editTreatmentsDetails.lotId,
                );
                dispatch(
                    treatmentActions.setGeneratedInvoiceCutoffDate(cutoffDate),
                );
            })();
        }
    }, [
        dispatch,
        feedlotId,
        editTreatmentsDetails.lotId,
        editTreatmentsDetails.date,
        cattleTreatmentId,
    ]);

    useEffect(() => {
        const isDateValid = thisDateWithinCutoff_deprecated(
            editTreatmentsDetails.date,
            generatedInvoiceCutoffDate,
        );
        setInvoiceAlreadyGeneratedForThisRecord(isDateValid);
    }, [
        generatedInvoiceCutoffDate,
        editTreatmentsDetails.date,
        setInvoiceAlreadyGeneratedForThisRecord,
    ]);

    useEffect(() => {
        return () => {
            dispatch(treatmentActions.resetState());
        };
    }, [dispatch]);

    useEffect(() => {
        const getPensByLot = async () => {
            if (
                feedlotId &&
                editTreatmentsDetails.lotId &&
                editTreatmentsDetails.date
            ) {
                const pens = await dispatch(
                    handleFindPenInformation(
                        feedlotId,
                        editTreatmentsDetails.lotId,
                        editTreatmentsDetails.date,
                        true,
                        true,
                    ),
                );
                dispatch(treatmentActions.setLotPens(pens));
            } else {
                dispatch(treatmentActions.setLotPens([]));
            }
        };

        getPensByLot();
    }, [
        dispatch,
        editTreatmentsDetails.date,
        editTreatmentsDetails.lotId,
        feedlotId,
    ]);
};

export const calculateDose_deprecated = (
    medicalItem: IMedicalItem,
    weight: number,
) => {
    const dose =
        medicalItem.dosageType === 'CC'
            ? Math.ceil((weight / 100) * medicalItem.dosageRate)
            : medicalItem.dosageRate;
    return dose;
};

export const calculateTotalPrice_deprecated = (
    medicalItem: IMedicalItem,
    dose: number,
) => {
    const totalPrice =
        Math.ceil(
            dose *
                (medicalItem.cost || 0) *
                (1 + (medicalItem.markup || 0) / 100) *
                100,
        ) / 100;
    return totalPrice;
};

export const useHasTreatmentRestrictions = () => {
    const { getUsersRoles } = useAuthContext();
    const usersRoles = getUsersRoles();

    return usersRoles.includes(Roles.COWBOY) && usersRoles.length === 1;
};

export const useCheckOriginalTreatmentRecord = () => {
    const { originalChargeoutItemsCollection } = useAppSelector(
        state => state.treatments,
    );
    const { cattleTreatmentId } = useParams();
    const isUpdateTreatmentRecord = cattleTreatmentId ? true : false;

    const medicalLotSelectedInOriginalTreatmentRecord = (
        medicalItemId?: number,
        medicalLotId?: string,
    ) => {
        return (
            isUpdateTreatmentRecord &&
            originalChargeoutItemsCollection.some(
                originalItem =>
                    originalItem.medicalItemId === medicalItemId &&
                    originalItem.medicalLotId === medicalLotId,
            )
        );
    };

    return { medicalLotSelectedInOriginalTreatmentRecord };
};
